import React, { useContext, useState } from "react";
import styled from "styled-components";
import { useRouter } from "next/router";
import { CheckoutContext } from "contexts/CheckoutProvider";
import { ReadingLevel } from "lib/api/types";
import { ReadingLevelImageMap } from "utils/readingLevelImageUtils";
import { guidedSignUpReplaceTemplateStrings } from "utils/guidedSignUpTemplating";
import StepLayout, {
  StepLayoutClassName,
  StepLayoutProps,
} from "components/Step/StepLayout";
import GuidedSignUpReadingLevelCard from "components/GuidedSignUp/GuidedSignUpReadingLevelCard";
import MembershipDialogFall25Coupon from "components/Membership/MembershipDialogFall25Coupon";
import { BudgetValues, GoalsValuesPartial } from "components/Step/stepTypes";
import {
  FALL25_COUPON_CODE,
  getOfferHeadingCssOverride,
  isBudgetRangeGreaterThan,
  isCouponClaimedByQuery,
} from "components/Step/stepUtil";
import { MembershipOfferBanner } from "components/Membership/MembershipOfferBanner";

/**
 * These configurations are simplified version of what existed in our contentful models.
 */
const config = {
  cardCopy: {
    bookTypesHeading: "Recommended book types",
    eyebrow: "Current Reading Level",
    skillsHeading: "Key skills to practice",
  },
  fallbackImage: {
    file: {
      url: "//images.ctfassets.net/vqtz4cq2w5ts/xIokKpS4iPJAecBMH0oM7/be44a34604882a085bec9dcd7b62d71f/guided_sign_up_confirmation_header_image.png",
      details: {
        size: 33380,
        image: {
          width: 300,
          height: 284,
        },
      },
    },
  },
  nextSteps: {
    title: "What happens next?",
    description:
      "Your first box is set to $name-possessive reading level and includes a variety of books—from the genres chosen as well as new ones to discover. Each book helps your reader develop key skills. When you rate each book in the Literati Kids App, we learn what your reader likes, helping to personalize future boxes!",
  },
};

/**
 * @note - styles are currently ported from old funnel for parity. We may want to update them in the future.
 */

const Container = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100vw;
`;

const StyledSection = styled.section`
  width: calc(100% - 2.5rem);
  max-width: 600px;
  margin: 2rem 0;
  padding: 0;
`;

const StyledH3 = styled.h3`
  font-weight: 800;
  text-align: center;
  font-size: 1rem;
`;

const StyledP = styled.p`
  margin: 0.8rem 0 0;
  font-size: 0.875rem;
`;

const StyledHeading = styled.div`
  margin-top: 2rem;
`;

export type StepLayoutPropsConfirmationReadingLevel = Omit<
  StepLayoutProps<object>,
  "asForm" | "children" | "formId"
>;

export interface StepConfirmationReadingLevelProps
  extends StepLayoutPropsConfirmationReadingLevel {
  budgetValues?: Partial<BudgetValues> | null;
  childName?: string;
  goalsValues?: GoalsValuesPartial | null;
  readingLevel?: ReadingLevel | null;
  readingLevelBadgesNoText?: ReadingLevelImageMap | null;
  showFall25Coupon?: boolean;
}

/**
 * @note - This step is currently specific for the simplified funnel version of onboarding.
 *         The version of this step that exists outside the funnel will continue to use
 *         the legacy component `GuidedSignUpLayoutConfirmation.js` until we separately
 *         update that flow.
 */
export const StepConfirmationReadingLevel = ({
  budgetValues,
  childName,
  goalsValues,
  readingLevel,
  readingLevelBadgesNoText,
  showFall25Coupon = false,
  ...stepLayoutProps
}: StepConfirmationReadingLevelProps) => {
  const router = useRouter();
  const { addPromoCode, getCheckoutSession, getOrderSummary } =
    useContext(CheckoutContext);
  const [isFall25CouponOpen, setIsFall25CouponOpen] = useState(false);
  const isBudgetRangeGreaterThanFifteen = isBudgetRangeGreaterThan(
    budgetValues,
    15
  );
  const isFall25CouponClaimed = isCouponClaimedByQuery(
    router.query,
    FALL25_COUPON_CODE
  );

  const readingLevelName = readingLevel?.name?.toLowerCase?.();
  const readingLevelImage = readingLevelName
    ? readingLevelBadgesNoText?.[readingLevelName]?.fields
    : config.fallbackImage;

  const showReadingLevelInfo = readingLevel && readingLevelImage;

  const heading = !showReadingLevelInfo
    ? ""
    : childName
    ? `Here’s what we recommend for ${childName}!`
    : "Here’s what we recommend for your child!";

  const nextStepsDescription = guidedSignUpReplaceTemplateStrings(
    config.nextSteps.description,
    { childName, readingLevelName }
  );

  const h =
    isBudgetRangeGreaterThanFifteen && showFall25Coupon ? (
      <div>
        <MembershipOfferBanner
          isClaimed={isFall25CouponClaimed}
          onClaimClick={() => setIsFall25CouponOpen(true)}
        />
        <StyledHeading>{heading}</StyledHeading>
      </div>
    ) : (
      heading
    );

  const cssOverride = getOfferHeadingCssOverride(
    isBudgetRangeGreaterThanFifteen && showFall25Coupon,
    StepLayoutClassName.HEADING
  );

  const handleClaimOffer = async () => {
    await router.push(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          extCouponCode: FALL25_COUPON_CODE,
        },
      },
      undefined,
      { shallow: true }
    );

    // Parity with select-plan Fall25 flow: URL + refetch alone left the cart without the promo;
    // also drives shouldOmitBudgetStepForAppliedFall25Coupon (session + URL) on funnel pages.
    await addPromoCode?.(FALL25_COUPON_CODE);
    await getOrderSummary?.();
    await getCheckoutSession?.();

    setIsFall25CouponOpen(false);
  };

  return (
    <>
      <StepLayout
        asForm={false}
        bgColor="white"
        css={cssOverride}
        disableContentSpacing
        heading={h}
        {...stepLayoutProps}
      >
        <Container>
          {showReadingLevelInfo ? (
            <GuidedSignUpReadingLevelCard
              readingLevel={readingLevel}
              cardCopy={config.cardCopy}
              readingLevelImage={readingLevelImage}
              goalsValues={goalsValues ?? undefined}
            />
          ) : null}
          <StyledSection>
            <StyledH3>{config.nextSteps.title}</StyledH3>
            <StyledP>{nextStepsDescription}</StyledP>
          </StyledSection>
        </Container>
      </StepLayout>
      <MembershipDialogFall25Coupon
        open={showFall25Coupon && isFall25CouponOpen}
        onOpenChange={setIsFall25CouponOpen}
        onClaimOffer={handleClaimOffer}
      />
    </>
  );
};

export default StepConfirmationReadingLevel;
